import { nextTick } from 'vue'
import { createRouter as createVueRouter, createWebHistory } from 'vue-router'
import progressBar from '@/utils/progressBar'
import routes from './routes'
import middleware from './middleware'

const router = createRouter({ base: '/' })

export default router

/**
 * The router factory
 */
function createRouter({ base }) {
    const router = createVueRouter({
        scrollBehavior,
        history: createWebHistory(base),
        routes,
    })

    router.beforeEach(beforeEach)
    router.afterEach(afterEach)
    // A thrown navigation error skips afterEach, which would leave the loading
    // bar trickling forever — always stop it, then recover from stale chunks.
    router.onError((error) => {
        progressBar.done()
        reloadOnStaleChunk(error)
    })

    return router
}

/**
 * A deploy can delete old hashed chunks while an open tab still references them,
 * so a lazy-loaded route fails to import. Vite surfaces this as a "Failed to
 * fetch dynamically imported module" error (Safari: "Importing a module script
 * failed"). Reload once to pick up the fresh index.html (time-guarded against
 * reload loops).
 *
 * @param {Error} error
 */
function reloadOnStaleChunk(error) {
    const message = error?.message || ''
    const chunkFailed = /failed to fetch dynamically imported module/i.test(message)
        || /error loading dynamically imported module/i.test(message)
        || /importing a module script failed/i.test(message)

    if (!chunkFailed) {
        return
    }

    const RELOADED_AT_KEY = 'live_caller__chunk_reloaded_at'

    // sessionStorage can throw (storage blocked, some private modes) — never let
    // the error handler itself crash; without the loop guard, skip the reload.
    try {
        const lastReload = Number(sessionStorage.getItem(RELOADED_AT_KEY) || 0)

        if (Date.now() - lastReload > 60000) {
            sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()))
            window.location.reload()
        }
    } catch (e) {
        console.warn('Chunk reload skipped, storage unavailable:', e)
    }
}

/**
 * Global router guard, return-style (vue-router deprecated the next()
 * callback): resolves to `true` to continue, `false` to abort, or a route
 * location to redirect.
 *
 * Middleware keep their existing API — `request.router.next(target)` now
 * resolves this guard with the target instead of calling the old callback.
 *
 * @param {Route} to
 * @param {Route} from
 * @return {Promise<boolean|string>}
 */
async function beforeEach(to, from) {
    // Get the matched components and resolve them.
    const components = await resolveComponents(getMatchedComponents(to))

    if (components.length === 0) {
        return true
    }

    // Start the loading bar.
    if (components[components.length - 1].loading !== false) {
        progressBar.start()
    }

    // Register & check middleware
    return new Promise((resolveGuard) => {
        const redirect = (target) => {
            resolveGuard(target)
            // legacy middleware chain .catch() onto this call
            return Promise.resolve()
        }

        to.matched.filter((record) => record.meta.middleware)
            .flatMap((route) => (Array.isArray(route.meta.middleware)
                ? route.meta.middleware
                : [route.meta.middleware]))
            .map((anonymousMiddleware) => {
                if (typeof anonymousMiddleware === 'string') {
                    let [name, parameters] = anonymousMiddleware.split(':', 2)
                    if (typeof parameters === 'string') {
                        parameters = parameters.split(',')
                    } else {
                        parameters = []
                    }
                    if (typeof middleware[name] === 'undefined') {
                        return (request, next) => {
                            next(request)
                        }
                    }
                    return (new middleware[name](parameters)).handle
                }
                if (typeof anonymousMiddleware === 'function') {
                    return anonymousMiddleware
                }
                if (typeof anonymousMiddleware === 'object'
                    && anonymousMiddleware instanceof middleware.baseMiddleware) {
                    return anonymousMiddleware.handle
                }

                return (request, next) => {
                    next(request)
                }
            })
            .reduce((current, next) => current.then((request) => new Promise((resolve, reject) => {
                next(request, resolve, reject)
            })), Promise.resolve({
                router: {
                    to,
                    from,
                    next: redirect,
                },
            }))
            .then(() => {
                resolveGuard(true)
            })
            .catch((e) => {
                console.warn(e)
                // Aborted navigation skips afterEach; stop the loading bar here
                // so it can't trickle forever.
                progressBar.done()
                resolveGuard(false)
            })
    })
}

/**
 * Global after hook.
 */
async function afterEach(to) {
    await nextTick()
    updateDocumentTitle(to)
    progressBar.done()
    setTimeout(() => {
        const contentWrapper = document.querySelector('.v-content__wrap')
        if (contentWrapper !== null) {
            contentWrapper.scrollTop = 0
        }
    }, 200)
}

/**
 * Build the tab title from the matched route records:
 * "Page Name · Subpage (if exists) · LiveCaller".
 *
 * @param {Route} to
 */
function updateDocumentTitle(to) {
    const titles = to.matched
        .map((record) => record.meta.title)
        .filter(Boolean)
        .filter((title, index, list) => title !== list[index - 1])

    document.title = [...titles, 'LiveCaller'].join(' · ')
}

/**
 * Resolve async components.
 *
 * @param  {Array} components
 * @return {Array}
 */
function resolveComponents(components) {
    return Promise.all(
        components.map((component) => (typeof component === 'function' ? component() : component)),
    )
}

/**
 * Scroll Behavior
 *
 * @link https://router.vuejs.org/en/advanced/scroll-behavior.html
 *
 * @param  {Route} to
 * @param  {Route} from
 * @param  {Object|undefined} savedPosition
 * @return {Object}
 */
function scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
        return savedPosition
    }

    if (to.hash) {
        return { el: to.hash }
    }

    const [component] = getMatchedComponents(to).slice(-1)

    if (component && component.scrollToTop === false) {
        return {}
    }

    return {
        left: 0,
        top: 0,
    }
}

/**
 * vue-router 4 removed router.getMatchedComponents().
 */
function getMatchedComponents(to) {
    return to.matched.flatMap((record) => Object.values(record.components || {}))
}
